export type Fundable360PaymentMethod = "paid_in_full" | "financed" | "subscription";

export type Fundable360CheckoutParams = {
  program: string;
  method: Fundable360PaymentMethod | null;
  rawMethod: string;
  ref: string;
  source: string;
  test: string;
  error: string | null;
};

const PAYMENT_METHODS = new Set<Fundable360PaymentMethod>([
  "paid_in_full",
  "financed",
  "subscription",
]);

/**
 * Parses a Fundable360 checkout URL without ever guessing a payment method.
 *
 * Some page builders can persist an already HTML-encoded query string, leaving
 * literal `&amp;` delimiters in the browser URL. Decode that one known form so a
 * valid financing link remains usable. Every other missing or unexpected method
 * fails closed instead of silently becoming the paid-in-full checkout.
 */
export function parseFundable360CheckoutParams(
  search: string
): Fundable360CheckoutParams {
  const rawParams = new URLSearchParams(search);
  const params =
    rawParams.has("method") || !/&amp;/i.test(search)
      ? rawParams
      : new URLSearchParams(search.replace(/&amp;/gi, "&"));

  const program = (params.get("program") ?? "").trim().toLowerCase();
  const rawMethod = (params.get("method") ?? "").trim();
  const method = PAYMENT_METHODS.has(rawMethod as Fundable360PaymentMethod)
    ? (rawMethod as Fundable360PaymentMethod)
    : null;

  let error: string | null = null;
  if (!program) {
    error =
      "No program selected. Please go back and choose a Fundable360 program.";
  } else if (!rawMethod) {
    error =
      "No payment option selected. Please go back and choose paid in full or 6-month financing.";
  } else if (!method) {
    error =
      "This checkout link has an invalid payment option. Please go back and choose your payment option again.";
  }

  return {
    program,
    method,
    rawMethod,
    ref: params.get("ref") ?? "",
    source: params.get("source") ?? "",
    test: params.get("test") ?? "",
    error,
  };
}
